import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { MicOff } from "lucide-react";
import { useDispatch } from "react-redux";

import { apiClient } from "@/api/apiClient";
import { getStreamingTokenApiV1TranscriptionStreamingTokenPost } from "@/api/generated/sdk.gen";
import { ConfirmationModal } from "@/components/ConfirmationModal";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { LIVE_TRANSCRIPTION } from "@/constants/live-transcription";
import { useTranscription } from "@/pages/ReportStudio/ReportBuilder/store/useTranscription";
import { setMessage } from "@/redux/notification";
import { cn } from "@/utils/ui";

import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";
import { ReactComponent as MicrophoneOnIcon } from "@assets/svg/mic-filled.svg";

import type { TranscriberProps, TranscriberRef } from "@components/Transcriber";
import {
  isRecordingSessionLive,
  shouldYieldToActiveTranscriber,
  showRecordingConflictMessage,
} from "@components/Transcriber/active-session";
import {
  LIVENESS_CHECK_INTERVAL_MS,
  MAX_RECONNECT_ATTEMPTS,
  RateLimitedStreamingError,
  SAMPLE_RATE,
  SILENCE_TOLERANCE_MS,
  type StreamingOptions,
  calculateReconnectBackoffMs,
  isStreamLikelyDead,
  isVoiceLevel,
  rateLimitMessage,
  retryAfterSeconds,
} from "@components/Transcriber/streaming";

import {
  type AaiServerMessage,
  type AaiTurn,
  applySpeakerRevision,
  classifyClose,
  isFinalTurn,
  normalizeSpeaker,
  normalizeSpeakerConfidence,
  parseServerMessage,
  toSpeakerWords,
} from "./protocol";
import {
  type SpeakerLiveTranscriptTurn,
  appendReconnectDivider,
  appendSpeakerTurn,
  fullTranscriptOf,
  insertSpeakerTurnInOrder,
  replaceTurn,
  turnKey,
} from "./segments";
import {
  CONSENT_REFRESH_INTERVAL_MS,
  KEEPALIVE_INTERVAL_MS,
  LOW_REALTIME_FACTOR_THRESHOLD,
  SPEECH_MODEL,
  TERMINATE_DRAIN_TIMEOUT_MS,
  buildAssemblyAIStreamingUrl,
} from "./streaming";

const SILENCE_TIMEOUT_MS = SILENCE_TOLERANCE_MS;
// No Begin within this window = failed handshake; otherwise start hangs on "Starting…" and the watchdog never arms.
const BEGIN_TIMEOUT_MS = 10000;

// STP-88 time-to-first-text marks, prefixed apart from the Azure engine's.
const PERF_PREFIX = "medirect.transcriber.assemblyai";
const perfMark = (name: string): void => {
  try {
    performance.mark(`${PERF_PREFIX}.${name}`);
  } catch {
    /* performance.mark missing in some test envs */
  }
};
const perfMeasure = (name: string, start: string, end: string): number => {
  try {
    const m = performance.measure(
      `${PERF_PREFIX}.${name}`,
      `${PERF_PREFIX}.${start}`,
      `${PERF_PREFIX}.${end}`
    );
    return m.duration;
  } catch {
    return -1;
  }
};

// Auth/account failures: retrying can't fix them, so skip the reconnect loop.
class FatalStreamingError extends Error {}

const applyHaloLevel = (halo: HTMLSpanElement | null, level: number) => {
  if (!halo) return;
  halo.style.opacity = String(0.15 + level * 0.55);
  halo.style.transform = `scale(${1 + level * 0.35})`;
};

// getTurns() actually returns SpeakerLiveTranscriptTurn[] (a structural superset).
export const AssemblyAITranscriber = forwardRef<TranscriberRef, TranscriberProps>(
  (
    {
      callback,
      disabled = false,
      buttonClassName,
      iconSize = "md",
      showLabel = false,
      label = "Transcribe",
      isPersistentSession = false,
    },
    ref
  ) => {
    const dispatch = useDispatch();
    const transcriberId = useId();
    const { setActiveTranscriber, isActive } = useTranscription();

    // Draining sockets are detached from this ref first, so nothing new is ever sent to them.
    const wsRef = useRef<WebSocket | null>(null);
    // Intentionally ended sockets; their close must not be mistaken for a drop.
    const retiredSocketsRef = useRef<WeakSet<WebSocket>>(new WeakSet());
    const lastConnectionIdRef = useRef<string | null>(null);

    const audioContextRef = useRef<AudioContext | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const micStreamRef = useRef<MediaStream | null>(null);
    const workletNodeRef = useRef<AudioWorkletNode | null>(null);
    const mixDestNodeRef = useRef<MediaStreamAudioDestinationNode | null>(null);
    const callSourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const shouldContinueRef = useRef(false);
    const silenceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const isReconnectingRef = useRef(false);
    const keepAliveTimerRef = useRef<NodeJS.Timeout | null>(null);
    const consentRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);

    const streamingOptionsRef = useRef<StreamingOptions>({});

    // Keyed by connection + turn_order so a SpeakerRevision can re-label in place.
    const liveTurnsRef = useRef<SpeakerLiveTranscriptTurn[]>([]);
    const sessionStartAtRef = useRef<number>(0);
    // Stale sockets / reconnect timers from an earlier session compare this and bail.
    const sessionGenRef = useRef(0);
    const lastTurnEndSecRef = useRef<number>(0);
    // Paused audio is neither sent nor recorded, so the timeline excludes paused time.
    const pausedAtRef = useRef<number | null>(null);
    const totalPausedMsRef = useRef(0);

    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    const latestPartialRef = useRef("");
    const latestPartialKeyRef = useRef<string | null>(null);
    // Turns committed early from a partial; their late final Turn must not re-append text.
    const flushedTurnKeysRef = useRef<Set<string>>(new Set());
    // Every turn already in the transcript; a late partial for one must never be re-appended.
    const committedTurnKeysRef = useRef<Set<string>>(new Set());
    // Breaks the connectWebSocket <-> attemptReconnect cycle.
    const attemptReconnectRef = useRef<() => void>(() => {});
    // Socket handlers capture handleTurn at connect time; read the live prop instead.
    const disabledRef = useRef(disabled);
    disabledRef.current = disabled;

    const lastFrameSentAtRef = useRef<number | null>(null);
    const lastResultAtRef = useRef<number>(0);
    const livenessTimerRef = useRef<NodeJS.Timeout | null>(null);
    const visibilityHandlerRef = useRef<(() => void) | null>(null);

    const audioLevelRef = useRef(0);
    const displayLevelRef = useRef(0);
    const levelRafIdRef = useRef<number | null>(null);
    // Styled directly per frame, not via a CSS var, to avoid restyling the button subtree ~60x/s.
    const haloRef = useRef<HTMLSpanElement | null>(null);

    const hasLoggedFirstTextRef = useRef(false);

    const [isTranscribing, setIsTranscribing] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const isPausedRef = useRef(false);
    const [isPaused, setIsPaused] = useState(false);
    const [showSilenceModal, setShowSilenceModal] = useState(false);
    const [unrecoverableError, setUnrecoverableError] = useState<
      null | "reconnect" | "auth"
    >(null);

    const getIconSizeClass = () => {
      switch (iconSize) {
        case "sm":
          return "size-3";
        case "lg":
          return "size-5";
        case "md":
        default:
          return "size-4";
      }
    };

    // Recorded time (wall clock minus pauses): the MediaRecorder and AssemblyAI audio-clock timeline.
    const recordedElapsedSec = useCallback((): number => {
      const now = performance.now();
      const pausedNow = pausedAtRef.current !== null ? now - pausedAtRef.current : 0;
      // A resumed recording continues the timeline of the audio captured before.
      const baseSec = Math.max(0, streamingOptionsRef.current.offsetSec ?? 0);
      return (
        baseSec +
        Math.max(
          0,
          (now - sessionStartAtRef.current - totalPausedMsRef.current - pausedNow) /
            1000
        )
      );
    }, []);

    const publishTurns = useCallback(() => {
      callbackRef.current.onTurnsUpdate?.(liveTurnsRef.current);
    }, []);

    const clearSilenceTimer = useCallback(() => {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    }, []);

    const startSilenceTimer = useCallback(() => {
      clearSilenceTimer();
      silenceTimerRef.current = setTimeout(() => {
        if (shouldContinueRef.current && wsRef.current) {
          setShowSilenceModal(true);
        }
      }, SILENCE_TIMEOUT_MS);
    }, [clearSilenceTimer]);

    const clearLivenessTimer = useCallback(() => {
      if (livenessTimerRef.current) {
        clearInterval(livenessTimerRef.current);
        livenessTimerRef.current = null;
      }
    }, []);

    const clearKeepAliveTimer = useCallback(() => {
      if (keepAliveTimerRef.current) {
        clearInterval(keepAliveTimerRef.current);
        keepAliveTimerRef.current = null;
      }
    }, []);

    const clearConsentRefreshTimer = useCallback(() => {
      if (consentRefreshTimerRef.current) {
        clearInterval(consentRefreshTimerRef.current);
        consentRefreshTimerRef.current = null;
      }
    }, []);

    const sendControl = useCallback(
      (type: "KeepAlive" | "ForceEndpoint" | "Terminate") => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        try {
          ws.send(JSON.stringify({ type }));
        } catch (err) {
          console.warn(`[AssemblyAITranscriber] ${type} send failed:`, err);
        }
      },
      []
    );

    const stopLevelRafLoop = useCallback(() => {
      if (levelRafIdRef.current !== null) {
        cancelAnimationFrame(levelRafIdRef.current);
        levelRafIdRef.current = null;
      }
      displayLevelRef.current = 0;
      applyHaloLevel(haloRef.current, 0);
    }, []);

    const startLevelRafLoop = useCallback(() => {
      if (levelRafIdRef.current !== null) return;
      const DECAY_PER_FRAME = 0.92;
      const tick = () => {
        const raw = audioLevelRef.current;
        const next =
          raw > displayLevelRef.current
            ? raw
            : displayLevelRef.current * DECAY_PER_FRAME;
        displayLevelRef.current = next;
        applyHaloLevel(haloRef.current, next);
        levelRafIdRef.current = requestAnimationFrame(tick);
      };
      levelRafIdRef.current = requestAnimationFrame(tick);
    }, []);

    // Detach + Terminate, but keep draining until Termination (or timeout): closing now
    // would drop late final Turns and the end-of-session SpeakerRevision.
    const retireSocket = useCallback((ws: WebSocket, graceful: boolean) => {
      retiredSocketsRef.current.add(ws);
      if (wsRef.current === ws) wsRef.current = null;
      if (!graceful || ws.readyState !== WebSocket.OPEN) {
        try {
          ws.close();
        } catch {}
        return;
      }
      try {
        ws.send(JSON.stringify({ type: "Terminate" }));
      } catch {
        try {
          ws.close();
        } catch {}
        return;
      }
      const timer = setTimeout(() => {
        try {
          ws.close(1000);
        } catch {}
      }, TERMINATE_DRAIN_TIMEOUT_MS);
      ws.addEventListener("close", () => clearTimeout(timer), { once: true });
    }, []);

    const cleanupTranscription = useCallback(() => {
      clearSilenceTimer();
      clearLivenessTimer();
      clearKeepAliveTimer();
      clearConsentRefreshTimer();
      stopLevelRafLoop();

      if (visibilityHandlerRef.current) {
        document.removeEventListener(
          "visibilitychange",
          visibilityHandlerRef.current
        );
        visibilityHandlerRef.current = null;
      }

      if (wsRef.current) {
        retireSocket(wsRef.current, true);
      }

      if (workletNodeRef.current) {
        try {
          workletNodeRef.current.disconnect();
        } catch {}
        workletNodeRef.current = null;
      }

      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch {}
        audioContextRef.current = null;
      }
      mixDestNodeRef.current = null;
      callSourceNodeRef.current = null;

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
        micStreamRef.current = null;
      }

      shouldContinueRef.current = false;
      reconnectAttemptsRef.current = 0;
      isReconnectingRef.current = false;
      lastFrameSentAtRef.current = null;
      lastResultAtRef.current = 0;
      hasLoggedFirstTextRef.current = false;
      isPausedRef.current = false;
      pausedAtRef.current = null;
      setIsPaused(false);
      setIsTranscribing(false);
      setIsProcessing(false);
    }, [
      clearSilenceTimer,
      clearLivenessTimer,
      clearKeepAliveTimer,
      clearConsentRefreshTimer,
      stopLevelRafLoop,
      retireSocket,
    ]);

    // Remembers the turn key so a late final Turn refreshes its display without duplicating text.
    const flushPartialAsFinal = useCallback(
      (notifyTextInsert: boolean) => {
        const text = latestPartialRef.current.trim();
        const key = latestPartialKeyRef.current;
        latestPartialRef.current = "";
        latestPartialKeyRef.current = null;
        if (text && !(key && committedTurnKeysRef.current.has(key))) {
          if (notifyTextInsert) {
            callbackRef.current.onTextInsert(text + " ", {});
          }
          const endSec = recordedElapsedSec();
          const flushKey = key ?? `flushed:${Date.now()}`;
          committedTurnKeysRef.current.add(flushKey);
          flushedTurnKeysRef.current.add(flushKey);
          liveTurnsRef.current = appendSpeakerTurn(liveTurnsRef.current, {
            key: flushKey,
            text,
            startSec: lastTurnEndSecRef.current,
            endSec,
          });
          lastTurnEndSecRef.current = endSec;
          publishTurns();
        }
        callbackRef.current.onPartialUpdate?.("");
      },
      [publishTurns, recordedElapsedSec]
    );

    const stopTranscription = useCallback(() => {
      // Covers a stop during startup, before the audio graph or socket exist.
      if (!wsRef.current && !audioContextRef.current && !shouldContinueRef.current)
        return;

      shouldContinueRef.current = false;
      setIsProcessing(true);

      flushPartialAsFinal(true);

      cleanupTranscription();

      if (isActive(transcriberId)) {
        setActiveTranscriber(null);
      }

      callbackRef.current.onEnd?.();

      dispatch(
        setMessage({
          content: "Transcription stopped successfully",
          type: "info",
        })
      );
    }, [
      cleanupTranscription,
      flushPartialAsFinal,
      dispatch,
      setActiveTranscriber,
      transcriberId,
      isActive,
    ]);

    // Socket stays open so turn_order and speakers carry over on resume. The AudioContext
    // is NOT suspended (MDRS-116); frames are dropped by the isPausedRef guard instead.
    const pauseTranscription = useCallback(() => {
      if (!shouldContinueRef.current || isPausedRef.current) return;
      isPausedRef.current = true;
      pausedAtRef.current = performance.now();
      setIsPaused(true);

      sendControl("ForceEndpoint");
      clearKeepAliveTimer();
      keepAliveTimerRef.current = setInterval(
        () => sendControl("KeepAlive"),
        KEEPALIVE_INTERVAL_MS
      );

      clearSilenceTimer();
      stopLevelRafLoop();
      audioLevelRef.current = 0;
    }, [clearSilenceTimer, clearKeepAliveTimer, sendControl, stopLevelRafLoop]);

    useEffect(() => {
      return () => {
        if (wsRef.current || audioContextRef.current || shouldContinueRef.current) {
          shouldContinueRef.current = false;
          cleanupTranscription();
        }
        if (isActive(transcriberId)) {
          setActiveTranscriber(null);
        }
      };
    }, [cleanupTranscription, transcriberId, isActive, setActiveTranscriber]);

    useEffect(() => {
      if (
        shouldYieldToActiveTranscriber({
          isTranscribing,
          isActiveTranscriber: isActive(transcriberId),
          isPersistentSession,
        })
      ) {
        stopTranscription();
      }
    }, [
      isTranscribing,
      isActive,
      transcriberId,
      stopTranscription,
      isPersistentSession,
    ]);

    // Reads wsRef at send time, so a reconnect needs no re-wiring.
    const wirePcmWorkletToSocket = useCallback(() => {
      if (!workletNodeRef.current) return;
      workletNodeRef.current.port.onmessage = (event) => {
        if (isPausedRef.current) return;

        const view = new Int16Array(event.data as ArrayBuffer);
        let peak = 0;
        for (let i = 0; i < view.length; i++) {
          const v = view[i] < 0 ? -view[i] : view[i];
          if (v > peak) peak = v;
        }
        audioLevelRef.current = peak / 32768;

        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        try {
          ws.send(event.data as ArrayBuffer);
        } catch (err) {
          console.warn("[AssemblyAITranscriber] Audio frame send failed:", err);
          return;
        }

        if (isVoiceLevel(audioLevelRef.current)) {
          lastFrameSentAtRef.current = Date.now();
        }
      };
    }, []);

    const acquireMicAndAudioGraph = useCallback(async (): Promise<void> => {
      if (workletNodeRef.current) {
        try {
          workletNodeRef.current.disconnect();
        } catch {}
        workletNodeRef.current = null;
      }
      mixDestNodeRef.current = null;
      callSourceNodeRef.current = null;
      if (audioContextRef.current) {
        try {
          await audioContextRef.current.close();
        } catch {}
        audioContextRef.current = null;
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((t) => t.stop());
        micStreamRef.current = null;
      }

      const audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
      audioContextRef.current = audioContext;

      const micDeviceId = streamingOptionsRef.current.micDeviceId;
      const audioConstraints = {
        channelCount: 1,
        sampleRate: SAMPLE_RATE,
        echoCancellation: true,
        noiseSuppression: true,
        latency: 0.05,
        ...(micDeviceId ? { deviceId: { exact: micDeviceId } } : {}),
      } as MediaTrackConstraints;

      const [micResult, workletResult] = await Promise.allSettled([
        navigator.mediaDevices.getUserMedia({ audio: audioConstraints }),
        audioContext.audioWorklet.addModule("/worklets/pcm-processor.js"),
      ]);

      if (micResult.status === "rejected") {
        throw micResult.reason;
      }
      if (workletResult.status === "rejected") {
        micResult.value.getTracks().forEach((t) => t.stop());
        throw workletResult.reason;
      }

      const micStream = micResult.value;
      micStreamRef.current = micStream;

      micStream.getTracks().forEach((track) => {
        track.onended = () => {
          if (!shouldContinueRef.current) return;
          console.warn(
            "[AssemblyAITranscriber] MediaStream track ended unexpectedly — attempting to re-acquire mic"
          );
          (async () => {
            try {
              await acquireMicAndAudioGraph();
              if (!wsRef.current) {
                attemptReconnectRef.current();
              }
            } catch (err) {
              console.error(
                "[AssemblyAITranscriber] Failed to re-acquire mic:",
                err
              );
              attemptReconnectRef.current();
            }
          })();
        };
      });

      const workletNode = new AudioWorkletNode(audioContext, "pcm-processor");
      workletNodeRef.current = workletNode;

      const extraAudioTrack = streamingOptionsRef.current.extraAudioTrack;
      const micSource = audioContext.createMediaStreamSource(micStream);
      let sessionStream = micStream;
      if (extraAudioTrack && extraAudioTrack.readyState === "live") {
        const mixDest = audioContext.createMediaStreamDestination();
        mixDestNodeRef.current = mixDest;
        const callSource = audioContext.createMediaStreamSource(
          new MediaStream([extraAudioTrack])
        );
        callSourceNodeRef.current = callSource;
        micSource.connect(mixDest);
        callSource.connect(mixDest);
        callSource.connect(workletNode);
        sessionStream = mixDest.stream;
      }
      micSource.connect(workletNode);
      mediaStreamRef.current = sessionStream;

      workletNode.connect(audioContext.destination);

      wirePcmWorkletToSocket();
    }, [wirePcmWorkletToSocket]);

    const resumeTranscription = useCallback(() => {
      if (!shouldContinueRef.current || !isPausedRef.current) return;

      clearKeepAliveTimer();
      if (pausedAtRef.current !== null) {
        totalPausedMsRef.current += performance.now() - pausedAtRef.current;
        pausedAtRef.current = null;
      }

      lastFrameSentAtRef.current = null;
      lastResultAtRef.current = Date.now();
      startLevelRafLoop();
      startSilenceTimer();

      const ctx = audioContextRef.current;
      if (!ctx || ctx.state === "closed" || !workletNodeRef.current) {
        acquireMicAndAudioGraph().catch((err) => {
          console.error(
            "[AssemblyAITranscriber] Failed to rebuild audio graph on resume:",
            err
          );
        });
      } else if (ctx.state === "suspended") {
        ctx.resume().catch((err) => {
          console.warn("[AssemblyAITranscriber] AudioContext resume failed:", err);
        });
      }

      isPausedRef.current = false;
      setIsPaused(false);
    }, [
      clearKeepAliveTimer,
      startLevelRafLoop,
      startSilenceTimer,
      acquireMicAndAudioGraph,
    ]);

    const startLivenessTimer = useCallback(() => {
      if (livenessTimerRef.current) {
        clearInterval(livenessTimerRef.current);
      }
      livenessTimerRef.current = setInterval(() => {
        if (!shouldContinueRef.current || !wsRef.current) return;
        if (isReconnectingRef.current) return;

        const audioContext = audioContextRef.current;
        if (
          audioContext &&
          audioContext.state === "suspended" &&
          !isPausedRef.current
        ) {
          console.warn(
            "[AssemblyAITranscriber] Liveness watchdog: AudioContext suspended unexpectedly — resuming"
          );
          audioContext.resume().catch((err) => {
            console.warn(
              "[AssemblyAITranscriber] AudioContext auto-resume failed:",
              err
            );
          });
        }

        // lastResultAtRef advances on every message incl. Heartbeat, so this only trips on a truly quiet socket.
        const dead = isStreamLikelyDead({
          lastTurnEventAt: lastResultAtRef.current,
          lastFrameSentAt: lastFrameSentAtRef.current,
          now: Date.now(),
        });
        if (dead) {
          console.warn(
            "[AssemblyAITranscriber] Liveness watchdog: speech flowing but no server messages — forcing reconnect"
          );
          attemptReconnectRef.current();
        }
      }, LIVENESS_CHECK_INTERVAL_MS);
    }, []);

    // Scribe-only: the backend requires the case and enforces ownership + recording consent.
    const fetchStreamingToken = useCallback(async (): Promise<string> => {
      const caseNumber = streamingOptionsRef.current.caseNumber;
      if (!caseNumber) {
        throw new FatalStreamingError("Scribe recording requires a case number");
      }
      const tokenResponse =
        await getStreamingTokenApiV1TranscriptionStreamingTokenPost({
          client: apiClient,
          body: { case_number: caseNumber },
        });
      const token = tokenResponse.data?.token;
      if (tokenResponse.error || !token) {
        const status = tokenResponse.response?.status;
        // 401/403 won't change on retry, so don't burn the reconnect budget.
        if (status === 401 || status === 403) {
          throw new FatalStreamingError(`Streaming token refused (HTTP ${status})`);
        }
        if (status === 429) {
          throw new RateLimitedStreamingError(
            retryAfterSeconds(tokenResponse.response)
          );
        }
        throw new Error("Failed to get AssemblyAI streaming token");
      }
      return token;
    }, []);

    const failSession = useCallback(
      (reason: "reconnect" | "auth") => {
        clearLivenessTimer();
        clearSilenceTimer();
        flushPartialAsFinal(true);
        setUnrecoverableError(reason);
        // Lets the recording session suspend (keep the audio) instead of recording silence.
        callbackRef.current.onFatalError?.();
        cleanupTranscription();
        if (isActive(transcriberId)) {
          setActiveTranscriber(null);
        }
        callbackRef.current.onEnd?.();
      },
      [
        clearLivenessTimer,
        clearSilenceTimer,
        flushPartialAsFinal,
        cleanupTranscription,
        isActive,
        transcriberId,
        setActiveTranscriber,
      ]
    );

    // Only a token fetch extends consent, so a long session without a reconnect needs this; the token is discarded.
    const startConsentRefreshTimer = useCallback(() => {
      clearConsentRefreshTimer();
      const generation = sessionGenRef.current;
      const stale = () =>
        !shouldContinueRef.current || generation !== sessionGenRef.current;
      consentRefreshTimerRef.current = setInterval(async () => {
        if (stale()) return;
        const refresh = async () => {
          try {
            await fetchStreamingToken();
          } catch (err) {
            if (stale()) return;
            if (err instanceof FatalStreamingError) {
              failSession("auth");
              return;
            }
            if (err instanceof RateLimitedStreamingError) {
              // Waiting a full interval would let the consent window lapse.
              setTimeout(() => {
                if (!stale()) void refresh();
              }, err.retryAfterSec * 1000);
              return;
            }
            console.warn(
              "[AssemblyAITranscriber] Consent refresh failed; retrying next interval:",
              err
            );
          }
        };
        await refresh();
      }, CONSENT_REFRESH_INTERVAL_MS);
    }, [clearConsentRefreshTimer, fetchStreamingToken, failSession]);

    const handleTurn = useCallback(
      (
        turn: AaiTurn,
        connectionId: string,
        offsetSec: number,
        draining: boolean
      ) => {
        const transcript = turn.transcript.trim();
        const key = turnKey(connectionId, turn.turn_order);

        if (transcript && !hasLoggedFirstTextRef.current) {
          hasLoggedFirstTextRef.current = true;
          perfMark("first-text");
          const ttft = perfMeasure("time-to-first-text", "start", "first-text");
          if (ttft > 0) {
            console.info(
              `[AssemblyAITranscriber] time-to-first-text: ${Math.round(ttft)}ms`
            );
          }
        }

        // Not while paused: pause's ForceEndpoint final Turn must not re-arm the timer.
        if (transcript && shouldContinueRef.current && !isPausedRef.current) {
          startSilenceTimer();
          setShowSilenceModal(false);
        }

        if (!isFinalTurn(turn)) {
          // Replace the partial, never append; ignored once draining.
          if (draining || !shouldContinueRef.current) return;
          // A late hypothesis (or unformatted end-of-turn twin) for a turn already committed.
          if (committedTurnKeysRef.current.has(key)) return;
          latestPartialRef.current = transcript;
          latestPartialKeyRef.current = key;
          callbackRef.current.onPartialUpdate?.(transcript);
          return;
        }

        const words = toSpeakerWords(turn.words, offsetSec);
        const speaker = normalizeSpeaker(turn.speaker_label);
        const speakerConfidence = normalizeSpeakerConfidence(
          turn.speaker_confidence
        );

        // Already committed from its partial: refresh display fields only.
        if (flushedTurnKeysRef.current.has(key)) {
          // Recovery/summary read the turns, so the final wording replaces the flushed text; text already inserted into the target can't be taken back.
          // The server dropped the audio behind the flushed partial: remove the phantom text.
          if (!transcript) {
            const remaining = liveTurnsRef.current.filter((t) => t.key !== key);
            if (remaining.length !== liveTurnsRef.current.length) {
              liveTurnsRef.current = remaining;
              publishTurns();
            }
            return;
          }
          const next = replaceTurn(liveTurnsRef.current, key, (t) => {
            const { speaker: _s, speakerConfidence: _c, words: _w, ...rest } = t;
            return {
              ...rest,
              text: transcript,
              ...(speaker ? { speaker } : {}),
              ...(speakerConfidence !== undefined ? { speakerConfidence } : {}),
              ...(words.length > 0 ? { words } : {}),
            };
          });
          if (next !== liveTurnsRef.current) {
            liveTurnsRef.current = next;
            publishTurns();
          }
          return;
        }

        // A repeated formatted final for a turn already in the transcript.
        if (committedTurnKeysRef.current.has(key)) return;

        // Don't blank a later turn's in-progress text that arrived first.
        const clearsShownPartial =
          latestPartialKeyRef.current === null ||
          latestPartialKeyRef.current === key;
        if (latestPartialKeyRef.current === key) {
          latestPartialRef.current = "";
          latestPartialKeyRef.current = null;
        }

        if (!transcript || disabledRef.current) {
          if (!draining && clearsShownPartial)
            callbackRef.current.onPartialUpdate?.("");
          return;
        }

        // After stop: keep transcript/turns complete, but don't insert into the closed target.
        if (!draining && shouldContinueRef.current) {
          callbackRef.current.onTextInsert(transcript + " ", {});
        }

        const startSec =
          words.length > 0 ? words[0].startSec : lastTurnEndSecRef.current;
        const endSec =
          words.length > 0 ? words[words.length - 1].endSec : recordedElapsedSec();
        committedTurnKeysRef.current.add(key);
        liveTurnsRef.current = insertSpeakerTurnInOrder(liveTurnsRef.current, {
          key,
          text: transcript,
          startSec,
          endSec,
          speaker,
          speakerConfidence,
          words,
        });
        lastTurnEndSecRef.current = Math.max(lastTurnEndSecRef.current, endSec);
        publishTurns();

        if (!draining && clearsShownPartial)
          callbackRef.current.onPartialUpdate?.("");
      },
      [publishTurns, recordedElapsedSec, startSilenceTimer]
    );

    const connectWebSocket = useCallback(
      (token: string): Promise<void> =>
        new Promise((resolve, reject) => {
          const { maxSpeakers } = streamingOptionsRef.current;
          const ws = new WebSocket(
            buildAssemblyAIStreamingUrl(token, {
              speakerLabels: LIVE_TRANSCRIPTION.assemblyai.speakerLabels,
              maxSpeakers,
              domain: LIVE_TRANSCRIPTION.assemblyai.domain,
            })
          );
          ws.binaryType = "arraybuffer";
          wsRef.current = ws;
          const generation = sessionGenRef.current;
          const isStaleSession = () => generation !== sessionGenRef.current;

          // turn_order and speaker labels restart per connection, so this state is per-socket.
          let connectionId: string | null = null;
          let offsetSec = 0;
          let lastErrorCode: number | undefined;
          let settled = false;

          const beginTimer = setTimeout(() => {
            if (settled) return;
            settled = true;
            console.warn("[AssemblyAITranscriber] No Begin within timeout:", {
              timeoutMs: BEGIN_TIMEOUT_MS,
            });
            retireSocket(ws, false);
            reject(new Error("Timed out waiting for Begin"));
          }, BEGIN_TIMEOUT_MS);

          const handleMessage = (msg: AaiServerMessage) => {
            // A newer session started; late messages belong to a finished session.
            if (isStaleSession()) return;
            const draining = retiredSocketsRef.current.has(ws);
            lastResultAtRef.current = Date.now();

            switch (msg.type) {
              case "Begin": {
                clearTimeout(beginTimer);
                const isFirstConnection = lastConnectionIdRef.current === null;
                connectionId = msg.id;
                lastConnectionIdRef.current = msg.id;
                // Anchor t=0 to Begin, not the Start click: the MediaRecorder only starts after the handshake.
                if (isFirstConnection && !draining) {
                  sessionStartAtRef.current = performance.now();
                  totalPausedMsRef.current = 0;
                }
                offsetSec = recordedElapsedSec();
                const model = msg.configuration?.model;
                if (model && model !== SPEECH_MODEL) {
                  console.warn(
                    "[AssemblyAITranscriber] Server applied a different model than requested:",
                    { requested: SPEECH_MODEL, applied: model }
                  );
                }
                if (draining) return;
                if (!shouldContinueRef.current) {
                  // Stopped while this (re)connect was in flight: don't resurrect the session.
                  retireSocket(ws, true);
                  settled = true;
                  reject(new Error("Session ended before the connection opened"));
                  return;
                }

                perfMark("ws-open");
                reconnectAttemptsRef.current = 0;
                isReconnectingRef.current = false;
                lastFrameSentAtRef.current = null;
                startLivenessTimer();
                if (!isPausedRef.current) startLevelRafLoop();

                setIsTranscribing(true);
                setIsProcessing(false);
                if (!isPausedRef.current) startSilenceTimer();

                settled = true;
                resolve();
                dispatch(
                  setMessage({
                    content: "Transcription started successfully",
                    type: "success",
                  })
                );
                return;
              }
              case "Turn":
                if (!connectionId) return;
                handleTurn(msg, connectionId, offsetSec, draining);
                return;
              case "SpeakerRevision": {
                if (!connectionId) return;
                const next = applySpeakerRevision(
                  liveTurnsRef.current,
                  connectionId,
                  msg
                );
                if (next !== liveTurnsRef.current) {
                  liveTurnsRef.current = next;
                  publishTurns();
                }
                return;
              }
              case "Heartbeat":
                if (
                  typeof msg.realtime_factor === "number" &&
                  msg.realtime_factor < LOW_REALTIME_FACTOR_THRESHOLD &&
                  !isPausedRef.current
                ) {
                  console.warn("[AssemblyAITranscriber] Under-delivering audio:", {
                    realtimeFactor: msg.realtime_factor,
                  });
                }
                return;
              case "Warning":
                // Non-fatal server notice (e.g. an ignored/invalid param).
                console.warn("[AssemblyAITranscriber] Server warning:", {
                  code: msg.warning_code,
                  warning: msg.warning,
                });
                return;
              case "SpeechStarted":
              case "Silence":
                // Liveness only; `Silence` needs `acknowledge_silence`, which we don't request.
                return;
              case "Termination":
                try {
                  ws.close(1000);
                } catch {}
                return;
              case "Error":
                lastErrorCode = msg.error_code;
                console.warn("[AssemblyAITranscriber] Server error:", {
                  code: msg.error_code,
                  error: msg.error,
                });
                return;
            }
          };

          ws.onmessage = (event: MessageEvent) => {
            if (typeof event.data !== "string") return;
            const msg = parseServerMessage(event.data);
            if (!msg) {
              console.warn("[AssemblyAITranscriber] Ignoring unrecognised message");
              return;
            }
            handleMessage(msg);
          };

          // `error` is always followed by `close`, which carries the code.
          ws.onerror = () => {};

          ws.onclose = (event: CloseEvent) => {
            clearTimeout(beginTimer);
            const retired = retiredSocketsRef.current.has(ws);
            if (wsRef.current === ws) wsRef.current = null;
            const disposition = classifyClose(event.code, lastErrorCode, retired);
            console.warn("[AssemblyAITranscriber] Socket closed:", {
              code: event.code,
              lastErrorCode,
              disposition,
            });

            if (!settled) {
              // Never reached Begin: the caller owns the retry decision via the rejection.
              settled = true;
              reject(
                disposition === "fatal"
                  ? new FatalStreamingError(`Socket refused (code ${event.code})`)
                  : new Error(`Socket closed before Begin (code ${event.code})`)
              );
              return;
            }

            if (retired || isStaleSession() || !shouldContinueRef.current) return;
            if (disposition === "fatal") {
              failSession("auth");
              return;
            }
            // Keep the mid-turn words; the dead connection's final Turn will never come.
            flushPartialAsFinal(true);
            attemptReconnectRef.current();
          };
        }),
      [
        dispatch,
        handleTurn,
        publishTurns,
        recordedElapsedSec,
        startLivenessTimer,
        startLevelRafLoop,
        startSilenceTimer,
        flushPartialAsFinal,
        failSession,
        retireSocket,
      ]
    );

    const attemptReconnect = useCallback(async () => {
      if (!shouldContinueRef.current) return;
      if (isReconnectingRef.current) return;

      if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        failSession("reconnect");
        return;
      }

      reconnectAttemptsRef.current += 1;
      isReconnectingRef.current = true;
      const delay = calculateReconnectBackoffMs(reconnectAttemptsRef.current);
      const generation = sessionGenRef.current;
      const abandoned = () =>
        !shouldContinueRef.current || generation !== sessionGenRef.current;

      // Speaker labels restart with the new connection — mark the seam.
      if (lastConnectionIdRef.current && liveTurnsRef.current.length > 0) {
        const next = appendReconnectDivider(
          liveTurnsRef.current,
          lastConnectionIdRef.current,
          recordedElapsedSec()
        );
        if (next !== liveTurnsRef.current) {
          liveTurnsRef.current = next;
          publishTurns();
        }
      }

      dispatch(
        setMessage({
          content: "Transcription interrupted. Attempting to reconnect...",
          type: "warning",
        })
      );

      setTimeout(async () => {
        if (abandoned()) return;

        try {
          // The old socket may be "open" but dead: keep mid-turn words, and retire it without
          // Terminate so its close can't re-trigger us.
          if (wsRef.current) {
            flushPartialAsFinal(true);
            retireSocket(wsRef.current, false);
          }

          const tracks = mediaStreamRef.current?.getTracks() ?? [];
          const allTracksLive =
            tracks.length > 0 && tracks.every((t) => t.readyState === "live");
          if (!allTracksLive) {
            await acquireMicAndAudioGraph();
            if (abandoned()) {
              // Stopped during getUserMedia: release the mic unless a newer session owns the graph.
              if (generation === sessionGenRef.current) cleanupTranscription();
              return;
            }
          }

          const token = await fetchStreamingToken();
          if (abandoned()) return;
          // Awaited so a pre-Begin failure lands in the catch, the only place isReconnectingRef resets.
          await connectWebSocket(token);
        } catch (err) {
          if (generation !== sessionGenRef.current) return;
          console.error("[AssemblyAITranscriber] Reconnect attempt failed:", err);
          isReconnectingRef.current = false;
          if (err instanceof FatalStreamingError) {
            failSession("auth");
            return;
          }
          if (err instanceof RateLimitedStreamingError) {
            // Waiting is the fix, so this attempt doesn't count against the budget.
            reconnectAttemptsRef.current = Math.max(
              0,
              reconnectAttemptsRef.current - 1
            );
            dispatch(
              setMessage({
                content: rateLimitMessage(err.retryAfterSec),
                type: "warning",
              })
            );
            setTimeout(() => {
              if (!abandoned()) attemptReconnect();
            }, err.retryAfterSec * 1000);
            return;
          }
          attemptReconnect();
        }
      }, delay);
    }, [
      dispatch,
      failSession,
      publishTurns,
      recordedElapsedSec,
      retireSocket,
      flushPartialAsFinal,
      cleanupTranscription,
      connectWebSocket,
      acquireMicAndAudioGraph,
      fetchStreamingToken,
    ]);

    useEffect(() => {
      attemptReconnectRef.current = attemptReconnect;
    }, [attemptReconnect]);

    const startTranscription = async (
      options: StreamingOptions = {}
    ): Promise<{
      mediaStream: MediaStream;
    } | null> => {
      if (isTranscribing || wsRef.current || disabled) return null;

      // MDRS-560: Scribe tokens are case-scoped; fail before prompting for the mic.
      if (!options.caseNumber) {
        dispatch(
          setMessage({
            content: "Recording needs a case. Open the case and try again.",
            type: "error",
          })
        );
        return null;
      }

      streamingOptionsRef.current = options;
      sessionGenRef.current += 1;
      const generation = sessionGenRef.current;
      const abandoned = () =>
        !shouldContinueRef.current || generation !== sessionGenRef.current;
      // A cancelled start is not an error: release what it acquired; no toast, no second onEnd.
      const bailOut = () => {
        if (generation === sessionGenRef.current) cleanupTranscription();
        return null;
      };

      liveTurnsRef.current = [];
      lastTurnEndSecRef.current = Math.max(0, options.offsetSec ?? 0);
      flushedTurnKeysRef.current = new Set();
      committedTurnKeysRef.current = new Set();
      lastConnectionIdRef.current = null;
      sessionStartAtRef.current = performance.now();
      totalPausedMsRef.current = 0;
      pausedAtRef.current = null;

      latestPartialRef.current = "";
      latestPartialKeyRef.current = null;
      isPausedRef.current = false;
      setIsPaused(false);
      setUnrecoverableError(null);

      setActiveTranscriber(transcriberId);

      callbackRef.current.onFocus?.();

      setIsProcessing(true);
      shouldContinueRef.current = true;
      perfMark("start");
      hasLoggedFirstTextRef.current = false;

      try {
        const startupResults = await Promise.allSettled([
          acquireMicAndAudioGraph(),
          fetchStreamingToken(),
        ]);

        if (abandoned()) return bailOut();

        const micSetup = startupResults[0];
        const tokenSettled = startupResults[1];

        if (micSetup.status === "rejected") throw micSetup.reason;
        if (tokenSettled.status === "rejected") throw tokenSettled.reason;

        perfMark("mic-acquired");
        perfMark("token-fetched");

        const handleVisibilityChange = () => {
          if (
            document.visibilityState === "visible" &&
            audioContextRef.current?.state === "suspended"
          ) {
            audioContextRef.current.resume().catch((err) => {
              console.warn(
                "[AssemblyAITranscriber] AudioContext resume failed:",
                err
              );
            });
          }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        visibilityHandlerRef.current = handleVisibilityChange;

        await connectWebSocket(tokenSettled.value);
        startConsentRefreshTimer();

        return { mediaStream: mediaStreamRef.current! };
      } catch (error) {
        // Stopped while the socket was opening: Begin rejected on purpose.
        if (abandoned()) return bailOut();

        console.error(
          "[AssemblyAITranscriber] Failed to start transcription:",
          error
        );

        cleanupTranscription();
        if (isActive(transcriberId)) {
          setActiveTranscriber(null);
        }
        callbackRef.current.onEnd?.();

        let errorMessage = "Failed to start transcription. Please try again.";

        if (error instanceof DOMException && error.name === "NotAllowedError") {
          errorMessage =
            "Microphone access denied. Please allow microphone access to use transcription.";
        } else if (error instanceof DOMException && error.name === "NotFoundError") {
          errorMessage =
            "No microphone detected. Please connect a microphone and try again.";
        } else if (
          error instanceof DOMException &&
          error.name === "OverconstrainedError"
        ) {
          errorMessage =
            "The selected microphone is no longer available. Please choose a different one and try again.";
        } else if (error instanceof FatalStreamingError) {
          errorMessage =
            "The transcription service refused the connection. Please confirm recording consent and try again.";
        } else if (error instanceof RateLimitedStreamingError) {
          errorMessage = rateLimitMessage(error.retryAfterSec);
        }

        dispatch(
          setMessage({
            content: errorMessage,
            type: "error",
          })
        );

        return null;
      }
    };

    const handleConfirmSilenceStop = useCallback(() => {
      setShowSilenceModal(false);
      stopTranscription();
    }, [stopTranscription]);

    const handleCancelSilenceStop = useCallback(() => {
      setShowSilenceModal(false);
      const audioContext = audioContextRef.current;
      if (audioContext && audioContext.state === "suspended") {
        audioContext.resume().catch((err) => {
          console.warn(
            "[AssemblyAITranscriber] AudioContext resume on silence-continue failed:",
            err
          );
        });
      }
      startSilenceTimer();
    }, [startSilenceTimer]);

    const replaceCallAudioTrack = useCallback((track: MediaStreamTrack) => {
      const audioContext = audioContextRef.current;
      const mixDest = mixDestNodeRef.current;
      const workletNode = workletNodeRef.current;
      if (!audioContext || !mixDest || !workletNode) return;

      if (callSourceNodeRef.current) {
        try {
          callSourceNodeRef.current.disconnect();
        } catch {}
      }
      const callSource = audioContext.createMediaStreamSource(
        new MediaStream([track])
      );
      callSourceNodeRef.current = callSource;
      callSource.connect(mixDest);
      callSource.connect(workletNode);
      streamingOptionsRef.current = {
        ...streamingOptionsRef.current,
        extraAudioTrack: track,
      };
    }, []);

    // Every user-initiated start must route through here.
    const startTranscriptionIfPermitted = (options?: StreamingOptions) => {
      if (!isPersistentSession && isRecordingSessionLive()) {
        showRecordingConflictMessage(dispatch);
        return;
      }
      startTranscription(options);
    };

    const handleToggleTranscription = () =>
      isTranscribing ? stopTranscription() : startTranscriptionIfPermitted();

    useImperativeHandle(
      ref,
      () => ({
        startTranscription,
        stopTranscription,
        pauseTranscription,
        resumeTranscription,
        isTranscribing,
        isPaused,
        replaceCallAudioTrack,
        getMicTrack: () => micStreamRef.current?.getAudioTracks()[0] ?? null,
        getFullTranscript: () => fullTranscriptOf(liveTurnsRef.current),
        getTurns: () => [...liveTurnsRef.current],
        getPartial: () => latestPartialRef.current,
      })
      // No deps: startTranscription is re-created each render, so the handle must be too.
    );

    return (
      <>
        <ConfirmationModal
          open={showSilenceModal}
          onOpenChange={(open) => {
            if (!open) {
              handleCancelSilenceStop();
            }
          }}
          type="warning"
          icon={<MicOff className="size-12 text-destructive" />}
          title="Stop Transcription?"
          description="No speech has been detected recently. Would you like to stop transcribing or continue?"
          confirmButtonText="Stop Transcription"
          cancelButtonText="Continue"
          onConfirm={handleConfirmSilenceStop}
          onCancel={handleCancelSilenceStop}
        />
        <ConfirmationModal
          // The recording session suspends and offers Resume instead.
          open={unrecoverableError !== null && !isPersistentSession}
          onOpenChange={(open) => {
            if (!open) setUnrecoverableError(null);
          }}
          type="warning"
          icon={<MicOff className="size-12 text-destructive" />}
          title="Dictation stopped"
          description={
            unrecoverableError === "auth"
              ? "The transcription service refused the connection (authorisation or recording consent). " +
                "Your transcript so far has been preserved. Tap Restart dictation to try again."
              : "We lost the connection to the transcription service and couldn't reconnect after multiple attempts. " +
                "Your transcript so far has been preserved. Tap Restart dictation to continue."
          }
          confirmButtonText="Restart dictation"
          cancelButtonText="Close"
          onConfirm={() => {
            setUnrecoverableError(null);
            // Keep the case (consent gate), mic and call audio; the timeline restarts.
            const { offsetSec: _offsetSec, ...options } =
              streamingOptionsRef.current;
            startTranscriptionIfPermitted(options);
          }}
          onCancel={() => setUnrecoverableError(null)}
        />
        <div className="flex items-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="relative inline-flex">
                {isTranscribing && (
                  <span
                    ref={haloRef}
                    aria-hidden="true"
                    // Reduced motion: brighten only, don't grow (!important beats the inline transform).
                    className="pointer-events-none absolute -inset-1 rounded-full bg-red-500 motion-reduce:!transform-none"
                    style={{
                      opacity: 0.15,
                      transform: "scale(1)",
                      transition: "transform 60ms linear, opacity 60ms linear",
                    }}
                  />
                )}
                <Button
                  variant={
                    showLabel
                      ? isTranscribing
                        ? "destructive-outline"
                        : "secondary-subtle"
                      : "ghost"
                  }
                  size={showLabel ? "sm" : "icon"}
                  className={cn(
                    "relative transition-colors",
                    !showLabel &&
                      cn(
                        "size-8 rounded-md",
                        isTranscribing
                          ? "bg-red-50 text-red-600 hover:bg-red-100"
                          : "hover:bg-gray-100 hover:text-gray-800"
                      ),
                    buttonClassName
                  )}
                  aria-label={
                    isTranscribing ? "stop transcribing" : "start transcription"
                  }
                  onClick={handleToggleTranscription}
                  disabled={isProcessing || disabled}
                >
                  {isProcessing ? (
                    <>
                      <LoadingIcon
                        className={cn(
                          showLabel ? "mr-1 size-4" : getIconSizeClass(),
                          "animate-spin-ccw text-primary-600"
                        )}
                      />
                      {showLabel && <span>Starting...</span>}
                    </>
                  ) : isTranscribing ? (
                    <>
                      {/* MDRS-266: filled mic, not mic-off. */}
                      <MicrophoneOnIcon
                        className={cn(
                          showLabel ? "mr-1 size-4" : getIconSizeClass(),
                          "[&_path]:fill-destructive"
                        )}
                      />
                      {showLabel && <span>Stop</span>}
                    </>
                  ) : (
                    <>
                      <MicrophoneOnIcon
                        className={showLabel ? "mr-1 size-4" : getIconSizeClass()}
                      />
                      {showLabel && <span>{label}</span>}
                    </>
                  )}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              {isProcessing
                ? "Starting dictation"
                : isTranscribing
                  ? "Stop dictation"
                  : "Start dictation"}
            </TooltipContent>
          </Tooltip>
        </div>
      </>
    );
  }
);

AssemblyAITranscriber.displayName = "AssemblyAITranscriber";
